import React, { useContext } from 'react';
import { CountryCode } from '@dayetopia/library/types';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import CartContents from '@components/cart/CartContents';
import CartEmpty from '@components/cart/CartEmpty';
import FreeShippingRequirement from '@components/cart/FreeShippingRequirement';
import { Context } from '@components/Context';
import SampleHolidayNotice from '@components/dashboard/components/SampleHolidayNotice';
import { NavContext } from '@components/navigation/Context';
import Button from '@components/shared/Button';
import Colors from '@components/shared/Colors';
import { P, Span } from '@components/shared/Typography';
import { NavStatus } from '@contracts';
import { itemsContainDTProduct } from '@utils/helpers';
import { getPriceText } from '@utils/price';
import useNavigate from '@utils/useNavigate';
import DonationAmountBanner from '../shared/design/DonationAmountBanner';

const NavBasketBox: React.FC = () => {
  const { t } = useTranslation();
  const { navigateWrapper } = useNavigate();
  const { state } = useContext(Context);
  const { navDispatch } = useContext(NavContext);
  const { cartItems, currency, price, country, shippingCost, discountAmount, loggedIn } = state;

  // Fix: Don't add discountAmount since it's already subtracted from price by useCheckOrderPrice
  const totalPrice = price + shippingCost.shippingPrice;

  const cartIncludesDTProduct = itemsContainDTProduct(cartItems);

  // Note: useApplyCouponFromUrl is now handled globally in Context.tsx

  const handleCheckout = () => {
    navDispatch({ type: 'setStatus', data: NavStatus.Default });
    if (loggedIn) {
      navigateWrapper('/checkout/order/');
    } else {
      navigateWrapper('/checkout/account/');
    }
  };

  return (
    <Container onClick={(e) => e.stopPropagation()}>
      <Heading>{t('components.your-basket')}</Heading>
      <FreeShippingRequirement location="basket" />
      {cartItems.length <= 0 ? (
        <CartEmpty />
      ) : (
        <>
          <CartContents checkout={false} textColor={Colors.navyBlue} />
          {cartIncludesDTProduct ? (
            <SampleNoticeWrap>
              <SampleHolidayNotice small={true} checkout={true} />
            </SampleNoticeWrap>
          ) : null}
          <CheckoutWrap>
            <DonationAmountBanner currencySymbol={currency.symbol} totalPrice={totalPrice} textKey="components.basket-donation" />
            <CheckoutRow>
              <TotalText>
                <StyledP>
                  {t('components.total')}: <Span $mods={['bold']}>{getPriceText(totalPrice, country, currency, t).fullString}</Span>
                </StyledP>
                {country !== CountryCode.US && <span className="include-vat">{t('components.all-prices-include-vat')}</span>}
              </TotalText>
              <Button onClick={handleCheckout} style={{ fontSize: 14 }}>
                {t('components.checkout')}
              </Button>
            </CheckoutRow>
          </CheckoutWrap>
        </>
      )}
    </Container>
  );
};

export default NavBasketBox;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  background-color: #fefce8;
  padding: 50px 0 140px 60px;
  height: 100%;
  overflow-y: scroll;
  width: 560px;

  @media only screen and (max-width: 768px) {
    width: 100vw;
    max-width: 100%;
    padding: 80px 0 120px;
  }
`;

const Heading = styled(P)`
  font-family: 'Domaine';
  width: 100%;
  font-size: 30px;
  font-weight: normal;
  line-height: 36px;
  letter-spacing: -0.02em;
  padding-bottom: 20px;
  border-bottom: 1px solid ${Colors.ecru};
  color: ${Colors.navyBlue};

  & + .cart-section {
    border-top: 0;
  }

  @media (max-width: 768px) {
    text-align: center;
    font-size: 24px;
    line-height: 26px;
    margin-bottom: 0;
  }
`;

const CheckoutWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 100%;
  padding-right: 60px;
  padding-top: 14px;
  margin-top: 20px;
  border-top: 1px solid #faead6;

  @media only screen and (max-width: 768px) {
    padding: 20px 30px;
  }
`;

const CheckoutRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const TotalText = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  span.include-vat {
    font-family: 'BrownStd';
    color: ${Colors.navyBlue};
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 10px;
    letter-spacing: 0.5px;
    text-align: left;
  }
`;

const StyledP = styled(P)`
  font-size: 22px;
  font-weight: 400;
  line-height: 32px;
  letter-spacing: 0;
  margin-bottom: 4px;
  color: ${Colors.navyBlue};

  @media (max-width: 768px) {
    font-size: 16px;
    line-height: 24px;
  }
`;

const SampleNoticeWrap = styled.div`
  padding-right: 60px;

  @media (max-width: 768px) {
    padding: 0 30px;
  }
`;
